import type { Connector } from '../shapes/Connector';
import type { Shape } from '../shapes/Shape';
import type { TextElement } from '../shapes/TextElement';
import { getConnectorEndArrowPoints, getConnectorPathData } from '../connector/ConnectorGeometry';
import { getConnectorLabelLayout, getConnectorLabelTheme } from '../connector/ConnectorLabelLayout';
import { fitTextToBox, getCenteredTextStartY, resolveTextAlignPosition } from '../tools/textLayout';
import { FONT_FAMILIES, LINE_HEIGHT_RATIO, PADDING } from '../../../config/constants';
import { parseTextLine, getFontSizeMultiplier, getFontWeight } from '../textEditor/TextFormatter';

export interface DiagramSvgRenderInput {
  translate: { x: number; y: number };
  shapes: Shape[];
  connectors: Connector[];
  textElements: TextElement[];
  measureCtx: CanvasRenderingContext2D;
  inverted: boolean;
}

export const renderDiagramSvgContent = (input: DiagramSvgRenderInput): string => {
  const { translate, connectors, shapes, textElements, measureCtx } = input;
  const { x: offsetX, y: offsetY } = translate;

  let content = `  <g transform="translate(${offsetX}, ${offsetY})">\n`;

  for (const connector of connectors) {
    content += renderConnectorSvg(connector, measureCtx, input.inverted);
  }

  for (const shape of shapes) {
    content += renderShapeSvg(shape, measureCtx);
  }

  for (const textElement of textElements) {
    content += renderTextElementSvg(textElement, measureCtx);
  }

  content += `  </g>\n`;
  return content;
};

const renderConnectorSvg = (
  connector: Connector,
  ctx: CanvasRenderingContext2D,
  inverted: boolean
): string => {
  let content = '';
  const pathData = getConnectorPathData({
    startPoint: connector.startPoint,
    endPoint: connector.endPoint,
    waypoints: connector.waypoints,
    arcPoint: connector.arcPoint,
  });
  content += `    <path d="${pathData}" fill="none" stroke="${connector.style.stroke}" stroke-width="${connector.style.strokeWidth}"`;
  if (connector.style.lineStyle === 'dashed') {
    content += ` stroke-dasharray="8,4"`;
  } else if (connector.style.lineStyle === 'dotted') {
    content += ` stroke-dasharray="2,4"`;
  }
  content += `/>\n`;

  if (connector.style.endArrow === 'arrow') {
    const endArrow = getConnectorEndArrowPoints({
      startPoint: connector.startPoint,
      endPoint: connector.endPoint,
      waypoints: connector.waypoints,
      arcPoint: connector.arcPoint,
    });
    const angle =
      Math.atan2(endArrow.to.y - endArrow.from.y, endArrow.to.x - endArrow.from.x) *
      180 /
      Math.PI;
    content += `    <polygon points="-10,-5 0,0 -10,5" fill="${connector.style.stroke}" transform="translate(${endArrow.to.x},${endArrow.to.y}) rotate(${angle})"/>\n`;
  }

  if (connector.text) {
    content += renderConnectorTextSvg(connector, ctx, inverted);
  }

  return content;
};

const renderShapeSvg = (shape: Shape, ctx: CanvasRenderingContext2D): string => {
  let content = '';
  const bounds = shape.getBounds();
  const style = shape.style;

  switch (shape.type) {
    case 'rectangle':
      content += `    <rect x="${bounds.x}" y="${bounds.y}" width="${bounds.width}" height="${bounds.height}" fill="${style.fill}" stroke="${style.stroke}" stroke-width="${style.strokeWidth}" rx="${style.cornerRadius || 0}"/>\n`;
      break;
    case 'ellipse':
      content += `    <ellipse cx="${bounds.x + bounds.width / 2}" cy="${bounds.y + bounds.height / 2}" rx="${bounds.width / 2}" ry="${bounds.height / 2}" fill="${style.fill}" stroke="${style.stroke}" stroke-width="${style.strokeWidth}"/>\n`;
      break;
    case 'diamond': {
      const cx = bounds.x + bounds.width / 2;
      const cy = bounds.y + bounds.height / 2;
      content += `    <polygon points="${cx},${bounds.y} ${bounds.x + bounds.width},${cy} ${cx},${bounds.y + bounds.height} ${bounds.x},${cy}" fill="${style.fill}" stroke="${style.stroke}" stroke-width="${style.strokeWidth}"/>\n`;
      break;
    }
    case 'triangle': {
      const tx = bounds.x + bounds.width / 2;
      content += `    <polygon points="${tx},${bounds.y} ${bounds.x + bounds.width},${bounds.y + bounds.height} ${bounds.x},${bounds.y + bounds.height}" fill="${style.fill}" stroke="${style.stroke}" stroke-width="${style.strokeWidth}"/>\n`;
      break;
    }
    case 'cylinder': {
      const ry = bounds.height * 0.15;
      content += `    <path d="M ${bounds.x} ${bounds.y + ry}
            A ${bounds.width / 2} ${ry} 0 0 1 ${bounds.x + bounds.width} ${bounds.y + ry}
            L ${bounds.x + bounds.width} ${bounds.y + bounds.height - ry}
            A ${bounds.width / 2} ${ry} 0 0 1 ${bounds.x} ${bounds.y + bounds.height - ry} Z" fill="${style.fill}" stroke="${style.stroke}" stroke-width="${style.strokeWidth}"/>\n`;
      content += `    <ellipse cx="${bounds.x + bounds.width / 2}" cy="${bounds.y + ry}" rx="${bounds.width / 2}" ry="${ry}" fill="${style.fill}" stroke="${style.stroke}" stroke-width="${style.strokeWidth}"/>\n`;
      break;
    }
  }

  content += renderShapeTextSvg(shape, ctx);
  return content;
};

const renderShapeTextSvg = (shape: Shape, ctx: CanvasRenderingContext2D): string => {
  if (!shape.text) return '';

  const { fontWeight, fontStyle, textAlign, color, fontFamily } = shape.textStyle;
  const bounds = shape.getBounds();
  const center = shape.getCenter();
  const padding = PADDING.shapeText;
  const maxWidth = shape.size.width - padding * 2;
  const maxHeight = shape.size.height - padding * 2;
  const fontSize = shape.textStyle.fontSize;
  const svgFontFamily = toSvgFontFamily(fontFamily);
  const layout = fitTextToBox(
    ctx,
    shape.text,
    maxWidth,
    maxHeight,
    {
      fontSize,
      fontWeight,
      fontStyle,
      fontFamily,
    },
    8,
    LINE_HEIGHT_RATIO
  );
  const lines = layout.lines;
  const baseLineHeight = layout.lineHeight;
  const resolvedFontSize = layout.fontSize;
  const { textX, textAnchor } = resolveTextAlignPosition(
    textAlign,
    bounds.x + padding,
    center.x,
    bounds.x + bounds.width - padding
  );
  const startY = getCenteredTextStartY(center.y, baseLineHeight, lines.length);

  let currentY = startY;
  return lines.map((line) => {
    const format = parseTextLine(line);
    const sizeMultiplier = getFontSizeMultiplier(format.type);
    const weight = getFontWeight(format.type, fontWeight);
    const adjustedFontSize = resolvedFontSize * sizeMultiplier;
    const lineHeight = baseLineHeight * sizeMultiplier;
    const indentOffset = format.indent * 12;

    const svgLine = `    <text x="${textX + indentOffset}" y="${currentY}" text-anchor="${textAnchor}" dominant-baseline="middle" font-family="${svgFontFamily}" font-size="${adjustedFontSize}" font-weight="${weight}" font-style="${fontStyle}" fill="${color}">${escapeXml(format.content)}</text>\n`;
    currentY += lineHeight;
    return svgLine;
  }).join('');
};

const renderTextElementSvg = (textElement: TextElement, ctx: CanvasRenderingContext2D): string => {
  const { fontSize, fontWeight, fontStyle, textAlign, color, fontFamily } = textElement.textStyle;
  const svgFontFamily = toSvgFontFamily(fontFamily);
  const bounds = textElement.getBounds(ctx);
  const lines = textElement.text.split('\n');
  const padding = PADDING.textElement;
  const baseLineHeight = fontSize * LINE_HEIGHT_RATIO;

  const centerY = textElement.position.y + bounds.height / 2;
  const { textX, textAnchor } = resolveTextAlignPosition(
    textAlign,
    textElement.position.x + padding,
    textElement.position.x + bounds.width / 2,
    textElement.position.x + bounds.width - padding
  );
  const startY = getCenteredTextStartY(centerY, baseLineHeight, lines.length);

  let currentY = startY;
  return lines.map((line) => {
    const format = parseTextLine(line);
    const sizeMultiplier = getFontSizeMultiplier(format.type);
    const weight = getFontWeight(format.type, fontWeight);
    const adjustedFontSize = fontSize * sizeMultiplier;
    const lineHeight = baseLineHeight * sizeMultiplier;
    const indentOffset = format.indent * 12;

    const svgLine = `    <text x="${textX + indentOffset}" y="${currentY}" text-anchor="${textAnchor}" dominant-baseline="middle" font-family="${svgFontFamily}" font-size="${adjustedFontSize}" font-weight="${weight}" font-style="${fontStyle}" fill="${color}">${escapeXml(format.content)}</text>\n`;
    currentY += lineHeight;
    return svgLine;
  }).join('');
};

const renderConnectorTextSvg = (
  connector: Connector,
  ctx: CanvasRenderingContext2D,
  inverted: boolean
): string => {
  const fontSize = connector.textSize;
  const svgFontFamily = FONT_FAMILIES.connectorSvg;
  const layout = getConnectorLabelLayout(ctx, connector);
  const theme = getConnectorLabelTheme(inverted);

  return (
    `    <rect x="${layout.rect.x}" y="${layout.rect.y}" width="${layout.rect.width}" height="${layout.rect.height}" fill="${theme.bgColor}"/>\n` +
    `    <text x="${layout.textX}" y="${layout.textY}" text-anchor="middle" dominant-baseline="middle" font-family="${svgFontFamily}" font-size="${fontSize}" fill="${theme.textColor}">${escapeXml(connector.text)}</text>\n`
  );
};

const escapeXml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const toSvgFontFamily = (fontFamily: string): string => fontFamily.replace(/"/g, "'");
